/* eslint-disable react/prop-types */
import React, { useRef } from "react"
import {
  makeStyles,
  Typography,
  Tooltip,
  IconButton,
  Grid,
  Button,
} from "@material-ui/core"
import CustomerDisplay from "components/sales-quotation/CustomerDisplay"
import EditIcon from "@material-ui/icons/Edit"
import ItemDisplay from "components/common/ItemDisplay"
import MUIDataTable from "mui-datatables"
import { Delete } from "@material-ui/icons"
import { formatDate } from "utils/form-data"
import Print from "@material-ui/icons/Print"
import { useReactToPrint } from "react-to-print"
import { numberFormat } from "utils/thousand-separator"
import ReceiptOfInvoicePrintLayout from "./print/ReceiptOfInvoicePrintLayout"

const useStyles = makeStyles((theme) => ({
  headerContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: "15px",
  },
  headerTitleContainer: {
    flexGrow: "1",
    margin: "0",
  },
  headerTitle: {
    fontWeight: "bold",
  },
  tableTitle: {
    margin: "0",
    marginLeft: "-20px",
    fontSize: theme.typography.body1.fontSize,
  },
  tableWrapper: {
    marginTop: "30px",
    marginBottom: "30px",
  },

  // Print Styles
  printWrapper: {
    padding: "12px",
    fontFamily: '"Arial", "Helvetica", "sans-serif"',
    color: "#000",
  },
  printPageBlock: {
    pageBreakInside: "avoid",
    marginBottom: 24,
  },
  printHeader: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  printLogo: {
    width: 60,
    height: 60,
    marginRight: 12,
  },
  printCompanyInfo: {
    lineHeight: 1.3,
  },
  printTitleCenter: {
    fontWeight: "bold",
    textAlign: "center",
    margin: "12px 0",
    textTransform: "uppercase",
  },
  printCustomerRow: {
    marginBottom: 8,
  },
  printTable: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: 4,
  },
  printThTd: {
    border: "1px solid #000",
    padding: "2px 4px",
    fontSize: "11px",
    textAlign: "center",
    verticalAlign: "middle",
  },
  printRowTotalAmount: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    textAlign: "left",
  },
  printFooterTable: {
    width: "100%",
    marginTop: 24,
  },
  printFooterCell: {
    verticalAlign: "top",
  },
  printFooterBox: {
    border: "1px solid #000",
    height: 80,
    padding: 8,
    marginBottom: 24,
  },
  printSignLineWrapper: {
    textAlign: "center",
    marginTop: 60,
  },
  printSignLine: {
    borderTop: "1px solid #000",
    width: "70%",
    margin: "0 auto 4px auto",
  },
  printDivider: {
    borderTop: "1px dashed #000",
    margin: "16px 0",
  },
}))

export default function ReceiptOfInvoiceForm({
  data,
  onConfirmEdit = () => {},
  onConfirmDelete = () => {},
  showEditAndDelete = false,
  canCreateROI = false,
  onConfirmCreateROI = () => {},
}) {
  const classes = useStyles()
  const componentRef = useRef(null)

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  })

  const title = `Receipt of Invoice`

  const generalFields = [
    // {
    //   attr: "idReceiptOfInvoice",
    //   label: "ROI Number",
    // },
    {
      attr: "createdAt",
      label: "Created At: ",
      display: (val) => (val ? formatDate(val) : "-"),
    },
    // {
    //   attr: "updatedAt",
    //   label: "Updated At",
    //   display: (val) => (val ? formatDate(val) : "-"),
    // },
    {
      attr: "totalAmountSum",
      label: "Total Amount: ",
      display: (val) => numberFormat(val || 0),
    },
  ]

  const customerFields = [
    {
      attr: "",
      label: "Customer: ",
      display: () =>
        data?.customer ? (
          <CustomerDisplay propName="custCode" customerData={data.customer} />
        ) : (
          <span style={{ color: "gray" }}>Unspecified</span>
        ),
    },
  ]

  const userFields = [
    {
      attr: "creator",
      label: "Created By: ",
    },
  ]

  const itemDisplayProps = {
    data,
    showEditButton: false,
    showDeleteButton: false,
  }

  const columns = [
    // {
    //   name: "no",
    //   label: "No",
    //   options: {
    //     setCellHeaderProps: () => ({
    //       style: { width: "1px" },
    //     }),
    //     customBodyRenderLite: (index) => index + 1,
    //   },
    // },
    {
      name: "invoiceNumber",
      label: "Invoice Number",
      options: {
        customBodyRenderLite: (index) =>
          data?.details?.[index]?.invoice?.invoiceNumber ?? "-",
      },
    },
    {
      name: "invoiceDate",
      label: "Invoice Date",
      options: {
        customBodyRenderLite: (index) => {
          const date = data?.details?.[index]?.invoice?.date
          return date ? formatDate(date) : "-"
        },
      },
    },
    {
      name: "currency",
      label: "Currency",
      options: {
        customBodyRenderLite: (index) =>
          data?.details?.[index]?.invoice?.currency?.code ?? "-",
      },
    },
    {
      name: "totalAmount",
      label: "Total Amount",
      options: {
        customBodyRenderLite: (index) => {
          const total = data?.details?.[index]?.totalAmount || 0
          return numberFormat(total)
        },
      },
    },
    {
      name: "documents",
      label: "Documents",
      options: {
        customBodyRenderLite: (index) => {
          const docs = data?.details?.[index]?.documents || []
          if (!docs.length) return "-"
          return (
            <ul style={{ paddingInlineStart: "16px", margin: 0 }}>
              {docs.map((doc) => (
                <li key={doc.idRoiDetailDocument}>{doc.documentName}</li>
              ))}
            </ul>
          )
        },
      },
    },
  ]

  const tableOptions = {
    elevation: 0,
    download: false,
    print: false,
    selectableRows: "none",
    responsive: "standard",
  }

  return (
    <>
      {/* HEADER + ACTION BUTTONS */}
      <div className={classes.headerContainer}>
        <div className={classes.headerTitleContainer}>
          <Typography variant="h6" className={classes.headerTitle}>
            {title}
          </Typography>
        </div>

        {/* Edit */}
        <Tooltip title="Edit">
          <IconButton
            variant="contained"
            color="primary"
            onClick={onConfirmEdit}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Delete */}
        {showEditAndDelete && data?.progress !== "post" && (
          <Tooltip title="Delete">
            <IconButton
              variant="contained"
              color="primary"
              onClick={onConfirmDelete}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Print */}
        <Tooltip title="Print ROI">
          <IconButton variant="contained" color="primary" onClick={handlePrint}>
            <Print fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Create ROI */}
        {canCreateROI && (
          <Button
            variant="contained"
            color="primary"
            style={{ marginLeft: "8px" }}
            onClick={onConfirmCreateROI}
          >
            Create Receipt of Invoice
          </Button>
        )}
      </div>

      {/* INFO SECTION */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ItemDisplay {...itemDisplayProps} attrsAndLabels={generalFields} />
        </Grid>
        <Grid item xs={12} md={4}>
          <ItemDisplay {...itemDisplayProps} attrsAndLabels={customerFields} />
        </Grid>
        <Grid item xs={12} md={4}>
          <ItemDisplay {...itemDisplayProps} attrsAndLabels={userFields} />
        </Grid>
      </Grid>

      {/* TABLE DETAIL ROI */}
      <div className={classes.tableWrapper}>
        <MUIDataTable
          title={
            <div className={classes.tableTitle}>
              <strong>Receipt of Invoice Detail</strong>
            </div>
          }
          data={data?.details || []}
          columns={columns}
          options={tableOptions}
        />
      </div>

      <div style={{ display: "none" }}>
        <ReceiptOfInvoicePrintLayout
          ref={componentRef}
          data={data}
          classes={classes}
        />
      </div>
    </>
  )
}
